/**
 * ProductHandleResolver
 *
 * Single source of truth for resolving a Shopify simple product handle
 * (color / size) from three sources in priority order:
 *
 *   1. Stored handle_color / handle_size in associated_id metafield
 *      (written by integrator after UNLISTED migration — no network call needed)
 *   2. /search/suggest.json by product ID
 *      (works only for active/listed products — fallback for older products)
 *   3. imprint-app backend endpoint /api/product/variants
 *      (works for UNLISTED products that have no stored handle yet)
 *
 * Usage:
 *   window.ProductHandleResolver.resolve(productId, associatedIds, 'color')
 *     .then(function(handle) { ... })
 */
window.ProductHandleResolver = {

  /**
   * @param {string|number|null} productId     - Shopify numeric product ID (id_color / id_size)
   * @param {Object|null}        associatedIds - parsed JSON from #associated_id DOM element
   * @param {string}             type          - 'color' or 'size'
   * @returns {Promise<string|null>}
   */
  resolve: function(productId, associatedIds, type) {
    var handleKey = type === 'color' ? 'handle_color' : 'handle_size';

    // --- 1. Stored handle in associated_id metafield (no network call) ---
    var storedHandle = associatedIds
      && associatedIds.simples
      && associatedIds.simples[handleKey];

    if (storedHandle) {
      console.log('[ProductHandleResolver] handle from metafield (' + type + '):', storedHandle);
      return Promise.resolve(storedHandle);
    }

    // --- 2. suggest.json fallback (active/listed products only) ---
    if (productId) {
      return fetch(
        '/search/suggest.json?q=id:' + productId
        + '&resources[type]=product&limit=1'
      )
        .then(function(res) { return res.json(); })
        .then(function(data) {
          var products = data
            && data.resources
            && data.resources.results
            && data.resources.results.products;

          if (products && products.length) {
            console.log('[ProductHandleResolver] handle from suggest.json (' + type + '):', products[0].handle);
            return products[0].handle;
          }

          // --- 3. backend /api/product/variants ---
          // For UNLISTED products that have no stored handle yet.
          return window.ProductHandleResolver.resolveFromBackend(productId, type);
        })
        .catch(function(err) {
          console.warn('[ProductHandleResolver] suggest.json error:', err);
          return null;
        });
    }

    console.warn('[ProductHandleResolver] no productId and no stored handle for type=' + type);
    return Promise.resolve(null);
  },

  /**
   * Backend fallback via imprint-app ProductInfoController.
   * Called when both the stored handle and suggest.json come up empty
   * (UNLISTED products created before the handle migration ran).
   *
   * @param {string|number} productId
   * @param {string}        type  - 'color' or 'size'
   * @returns {Promise<string|null>}
   */
  resolveFromBackend: function(productId, type) {
    var settings   = (window.PXUTheme && window.PXUTheme.theme_settings) || {};
    var appUrl     = settings.api_beckend_url || '';
    var shopDomain = settings.api_shop_domain || '';
    var shopName   = shopDomain.replace('.myshopify.com', '');

    if (!appUrl || !shopDomain) {
      console.warn('[ProductHandleResolver] resolveFromBackend: missing api_beckend_url or api_shop_domain theme setting');
      return Promise.resolve(null);
    }

    // appUrl may or may not have a trailing slash — normalize before joining
    var base = appUrl.replace(/\/+$/, '');

    return fetch(
      base + '/api/product/variants'
      + '?product_id=' + productId
      + '&shop_domain=' + encodeURIComponent(shopDomain)
      + '&shop=' + encodeURIComponent(shopName)
    )
      .then(function(res) {
        if (!res.ok) {
          console.warn('[ProductHandleResolver] resolveFromBackend HTTP ' + res.status + ' for id=' + productId);
          return null;
        }
        return res.json();
      })
      .then(function(data) {
        if (data && data.handle) {
          console.log('[ProductHandleResolver] handle from backend (' + type + '):', data.handle);
          return data.handle;
        }
        console.warn('[ProductHandleResolver] resolveFromBackend: no handle in response for id=' + productId);
        return null;
      })
      .catch(function(err) {
        console.warn('[ProductHandleResolver] resolveFromBackend error:', err);
        return null;
      });
  }
};
